import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Clock, Calendar as CalendarIcon, Plus, Trash2, Loader2, Save, Edit2, X, ToggleLeft, ToggleRight, Split, Minus, Sun, Moon } from 'lucide-react';
import { format } from 'date-fns';
import { useEvents } from '@/contexts/EventsContext';
import { useAuth } from '@/contexts/AuthContext';
import { toast } from 'sonner';
import DatePicker from '../ui/DatePicker';
import TimePicker from '../ui/TimePicker';
import CustomSelect from '../ui/CustomSelect';
import ConfirmDialog from '../ui/ConfirmDialog';
import { useTranslation } from 'react-i18next';
import { DEFAULT_OPEN_TIME, DEFAULT_CLOSE_TIME, DEFAULT_BREAK_START, DEFAULT_BREAK_END, DAYS_ORDER } from '@/lib/constants';

interface SalonHoursProps {
    organization: any;
    readOnly?: boolean;
}

type DaySchedule = {
    isOpen: boolean;
    isSplit: boolean;
    start1: string;
    end1: string;
    start2: string;
    end2: string;
};

const SalonHours: React.FC<SalonHoursProps> = ({ organization, readOnly = false }) => {
    const { t } = useTranslation();
    const { busySlots, createBusySlot, deleteBusySlot } = useEvents();
    const { updateOrganization } = useAuth();

    const [isEditing, setIsEditing] = useState(false);
    const [isSaving, setIsSaving] = useState(false);

    // State for holidays
    const [start, setStart] = useState<Date | undefined>();
    const [end, setEnd] = useState<Date | undefined>();
    const [isAddingHoliday, setIsAddingHoliday] = useState(false);
    const [holidayToDelete, setHolidayToDelete] = useState<string | null>(null);

    const parseSchedule = (org: any) => {
        if (!org?.time_ranges) return {};
        const newSchedule: any = {};
        DAYS_ORDER.forEach(key => {
            const time = org.time_ranges[key] || {};
            const isOpen = !!time.open;
            const isSplit = !!(time.start_break && time.end_break);

            newSchedule[key] = {
                isOpen,
                isSplit,
                start1: time.open || DEFAULT_OPEN_TIME,
                end1: isSplit ? (time.start_break || DEFAULT_BREAK_START) : (time.close || DEFAULT_CLOSE_TIME),
                start2: time.end_break || DEFAULT_BREAK_END,
                end2: time.close || DEFAULT_CLOSE_TIME
            };
        });
        return newSchedule;
    };

    // Initialize schedule state
    const [schedule, setSchedule] = useState<Record<string, DaySchedule>>(() => parseSchedule(organization));

    // Update schedule if organization changes
    useEffect(() => {
        setSchedule(parseSchedule(organization));
    }, [organization]);

    const holidays = busySlots ? busySlots.filter((s: any) => s.type === 'holiday' && !s.hairdresser_id) : [];

    // Schedule Handlers
    const toggleOpen = (day: string) => {
        setSchedule(prev => ({ ...prev, [day]: { ...prev[day], isOpen: !prev[day].isOpen } }));
    };

    const toggleSplitShift = (day: string) => {
        setSchedule(prev => ({
            ...prev,
            [day]: {
                ...prev[day],
                isSplit: !prev[day].isSplit,
                end1: !prev[day].isSplit ? DEFAULT_BREAK_START : DEFAULT_CLOSE_TIME
            }
        }));
    };

    const updateTime = (day: string, field: keyof DaySchedule, value: string) => {
        setSchedule(prev => ({ ...prev, [day]: { ...prev[day], [field]: value } }));
    };

    const saveHours = async () => {
        setIsSaving(true);
        const newTimeRanges: any = {};
        let isValid = true;

        DAYS_ORDER.forEach(key => {
            if (!isValid) return; // Skip checks if already invalid

            const cfg = schedule[key];
            if (!cfg.isOpen) {
                newTimeRanges[key] = { open: null, close: null, start_break: null, end_break: null };
            } else {
                // Validation Logic
                if (cfg.isSplit) {
                    // Check strict ordering: start1 < end1 < start2 < end2
                    if (!(cfg.start1 < cfg.end1)) {
                        toast.error(t('staff.hours.error_morning', { day: t(`common.days.${key}`) }));
                        isValid = false;
                    } else if (!(cfg.end1 < cfg.start2)) {
                        toast.error(t('staff.hours.error_break', { day: t(`common.days.${key}`) }));
                        isValid = false;
                    } else if (!(cfg.start2 < cfg.end2)) {
                        toast.error(t('staff.hours.error_afternoon', { day: t(`common.days.${key}`) }));
                        isValid = false;
                    }

                    if (isValid) {
                        newTimeRanges[key] = { open: cfg.start1, start_break: cfg.end1, end_break: cfg.start2, close: cfg.end2 };
                    }
                } else {
                    // Continuous: start1 < end1
                    if (!(cfg.start1 < cfg.end1)) {
                        toast.error(t('staff.hours.error_continuous', { day: t(`common.days.${key}`) }));
                        isValid = false;
                    }

                    if (isValid) {
                        newTimeRanges[key] = { open: cfg.start1, close: cfg.end1, start_break: null, end_break: null };
                    }
                }
            }
        });

        if (!isValid) {
            setIsSaving(false);
            return;
        }

        try {
            const res = await updateOrganization({ ...organization, time_ranges: newTimeRanges });
            if (res.success) {
                toast.success(t('staff.hours.toast.saved'));
                setIsEditing(false);
            } else {
                toast.error(t('common.save_error'));
            }
        } catch (e) { toast.error(t('common.error')); }
        finally { setIsSaving(false); }
    };

    // Holidays Handlers
    const handleCancel = () => {
        setSchedule(parseSchedule(organization));
        setIsEditing(false);
    };

    const addHoliday = async () => {
        if (!start || !end) return toast.error(t('staff.timeoff.toast.dates_missing'));
        setIsAddingHoliday(true);
        try {
            const res = await createBusySlot({ shop_id: organization.id, type: 'holiday', start_date: format(start, 'yyyy-MM-dd'), end_date: format(end, 'yyyy-MM-dd') });
            if (res.success) { toast.success(t('staff.timeoff.toast.added')); setStart(undefined); setEnd(undefined); }
            else toast.error(t('common.error'));
        } catch (e) { toast.error(t('common.error')); } finally { setIsAddingHoliday(false); }
    };

    const deleteHoliday = async () => {
        if (!holidayToDelete) return;
        await deleteBusySlot(holidayToDelete);
        toast.success(t('staff.timeoff.toast.deleted'));
        setHolidayToDelete(null);
    };

    return (
        <motion.div variants={{ hidden: { opacity: 0, y: 10 }, visible: { opacity: 1, y: 0 } }} className="grid grid-cols-1 lg:grid-cols-3 gap-6">

            {/* Orari */}
            <div className="lg:col-span-2 bg-white rounded-3xl border border-gray-100 shadow-sm p-8">
                <div className="flex justify-between items-center mb-6">
                    <h3 className="text-xl font-bold text-gray-900 flex items-center gap-2">
                        <Clock className="text-clip-500" size={20} /> {t('salon.hours.opening_hours')}
                    </h3>
                    {!readOnly && (
                        <button
                            onClick={isEditing ? handleCancel : () => setIsEditing(true)}
                            className={`flex items-center gap-2 px-3 py-1.5 rounded-lg text-xs font-bold transition-all cursor-pointer ${isEditing ? 'bg-red-50 text-red-600 border border-red-100' : 'bg-gray-50 border border-gray-200 text-gray-700 hover:bg-gray-100'}`}
                        >
                            {isEditing ? <><X size={14} /> {t('common.cancel')}</> : <><Edit2 size={14} /> {t('common.edit')}</>}
                        </button>
                    )}
                </div>

                <div className="space-y-4">
                    {DAYS_ORDER.map(key => {
                        const dayConfig = schedule[key];
                        // Fallback if data sync hasn't happened yet or initial loading
                        if (!dayConfig) return null;

                        const isOpen = dayConfig.isOpen;
                        const isSplit = dayConfig.isSplit;

                        return (
                            <div key={key} className={`bg-white border rounded-2xl p-4 transition-all ${isOpen ? 'border-gray-200 shadow-sm' : 'border-gray-100 opacity-60 bg-gray-50/50'}`}>
                                {/* HEADER GIORNO */}
                                <div className="flex justify-between items-center mb-2">
                                    <div className="flex items-center gap-4">
                                        <span className={`w-24 font-bold ${isOpen ? 'text-gray-900 capitalize' : 'text-gray-400 capitalize'}`}>{t(`common.days.${key}`)}</span>
                                    </div>
                                    {/* TOGGLE MOVED TO RIGHT (Outside the gap-4 div) */}
                                    {isEditing ? (
                                        <button onClick={() => toggleOpen(key)} className={`flex items-center gap-2 text-xs font-bold px-3 py-1.5 rounded-lg transition-colors ${isOpen ? 'bg-green-50 text-green-700' : 'bg-gray-200 text-gray-500'}`}>
                                            {isOpen ? <ToggleRight size={18} /> : <ToggleLeft size={18} />}
                                            <span className="hidden sm:inline">{isOpen ? t('staff.hours.working') : t('staff.hours.rest')}</span>
                                        </button>
                                    ) : (
                                        !isOpen ? <span className="text-xs font-bold text-gray-400 bg-gray-100 px-2 py-1 rounded-md uppercase">{t('common.closed')}</span> : <span className="text-xs font-bold text-green-600 bg-green-50 px-2 py-1 rounded-md uppercase">{t('common.open')}</span>
                                    )}
                                </div>

                                {/* CONTENUTO ORARI */}
                                {isOpen && (
                                    <div className="mt-4 pt-4 border-t border-gray-50">
                                        {isEditing ? (
                                            <div className="space-y-4">
                                                {/* TOGGLE SPLIT / CONTINUOUS */}
                                                <div className="flex justify-center bg-gray-50 p-1 rounded-lg">
                                                    <button onClick={() => !isSplit && toggleSplitShift(key)} className={`flex-1 flex items-center justify-center gap-2 py-1.5 rounded-md text-xs font-bold transition-all ${isSplit ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-400 hover:text-gray-600'}`}><Split size={14} /> {t('staff.hours.split')}</button>
                                                    <button onClick={() => isSplit && toggleSplitShift(key)} className={`flex-1 flex items-center justify-center gap-2 py-1.5 rounded-md text-xs font-bold transition-all ${!isSplit ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-400 hover:text-gray-600'}`}><Minus size={14} /> {t('staff.hours.continuous')}</button>
                                                </div>

                                                {/* INPUT GRID */}
                                                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                                                    {isSplit ? (
                                                        <>
                                                            {/* MATTINA */}
                                                            <div className="bg-gray-50 p-3 rounded-xl border border-gray-100 flex flex-col gap-2">
                                                                <div className="flex items-center gap-2 text-xs font-bold text-gray-500 uppercase tracking-wider">
                                                                    <Sun size={14} className="text-orange-400" />
                                                                    {t('staff.hours.morning')}
                                                                </div>
                                                                <div className="flex items-center gap-2 w-full">
                                                                    <div className="flex-1 min-w-0"><TimePicker value={dayConfig.start1} onChange={(val) => updateTime(key, 'start1', val)} minTime={DEFAULT_OPEN_TIME} maxTime={DEFAULT_CLOSE_TIME} step={5} /></div>
                                                                    <span className="text-gray-300 font-bold shrink-0">-</span>
                                                                    <div className="flex-1 min-w-0"><TimePicker value={dayConfig.end1} onChange={(val) => updateTime(key, 'end1', val)} minTime={DEFAULT_OPEN_TIME} maxTime={DEFAULT_CLOSE_TIME} step={5} /></div>
                                                                </div>
                                                            </div>

                                                            {/* POMERIGGIO */}
                                                            <div className="bg-gray-50 p-3 rounded-xl border border-gray-100 flex flex-col gap-2">
                                                                <div className="flex items-center gap-2 text-xs font-bold text-gray-500 uppercase tracking-wider">
                                                                    <Moon size={14} className="text-indigo-400" />
                                                                    {t('staff.hours.afternoon')}
                                                                </div>
                                                                <div className="flex items-center gap-2 w-full">
                                                                    <div className="flex-1 min-w-0"><TimePicker value={dayConfig.start2} onChange={(val) => updateTime(key, 'start2', val)} minTime={DEFAULT_OPEN_TIME} maxTime={DEFAULT_CLOSE_TIME} step={5} /></div>
                                                                    <span className="text-gray-300 font-bold shrink-0">-</span>
                                                                    <div className="flex-1 min-w-0"><TimePicker value={dayConfig.end2} onChange={(val) => updateTime(key, 'end2', val)} minTime={DEFAULT_OPEN_TIME} maxTime={DEFAULT_CLOSE_TIME} step={5} /></div>
                                                                </div>
                                                            </div>
                                                        </>
                                                    ) : (
                                                        /* CONTINUATO */
                                                        <div className="col-span-1 md:col-span-2 bg-gray-50 p-3 rounded-xl border border-gray-100 flex flex-col gap-2">
                                                            <div className="flex items-center gap-2 text-xs font-bold text-gray-500 uppercase tracking-wider">
                                                                <Clock size={14} className="text-clip-500" />
                                                                {t('staff.hours.schedule')}
                                                            </div>
                                                            <div className="flex items-center gap-2 w-full">
                                                                <div className="flex-1 min-w-0"><TimePicker value={dayConfig.start1} onChange={(val) => updateTime(key, 'start1', val)} minTime={DEFAULT_OPEN_TIME} maxTime={DEFAULT_CLOSE_TIME} step={5} /></div>
                                                                <span className="text-gray-300 font-bold shrink-0">-</span>
                                                                <div className="flex-1 min-w-0"><TimePicker value={dayConfig.end1} onChange={(val) => updateTime(key, 'end1', val)} minTime={DEFAULT_OPEN_TIME} maxTime={DEFAULT_CLOSE_TIME} step={5} /></div>
                                                            </div>
                                                        </div>
                                                    )}
                                                </div>
                                            </div>
                                        ) : (
                                            <div className="text-sm text-gray-600 font-medium pl-0">
                                                {isSplit ? (
                                                    <div className="flex flex-col sm:flex-row sm:items-center sm:gap-3">
                                                        <span>{dayConfig.start1} - {dayConfig.end1}</span>
                                                        <span className="text-gray-300 hidden sm:inline">/</span>
                                                        <span>{dayConfig.start2} - {dayConfig.end2}</span>
                                                    </div>
                                                ) : (
                                                    <span>{dayConfig.start1} - {dayConfig.end1} ({t('staff.hours.continuous')})</span>
                                                )}
                                            </div>
                                        )}
                                    </div>
                                )}
                            </div>
                        );
                    })}
                </div>
                {isEditing && (
                    <div className="mt-6 pt-4 border-t border-gray-100 flex justify-end animate-in fade-in">
                        <button onClick={saveHours} disabled={isSaving} className="bg-black text-white px-6 py-2 rounded-xl font-bold text-sm shadow-lg hover:bg-gray-800 flex items-center gap-2 cursor-pointer">
                            {isSaving ? <Loader2 className="animate-spin" size={14} /> : <Save size={14} />} {t('staff.hours.save')}
                        </button>
                    </div>
                )}
            </div>

            {/* Colonna di destra: Impostazioni Time Step & Ferie */}
            <div className="space-y-6 flex flex-col">
                {/* Impostazioni Time Step */}
                {!readOnly && (
                    <div className="bg-white rounded-3xl border border-gray-100 shadow-sm p-8">
                        <div className="flex justify-between items-center mb-6">
                            <h3 className="text-xl font-bold text-gray-900 flex items-center gap-2">
                                <Clock className="text-indigo-500" size={20} />
                                {t('salon.hours.time_step')}
                            </h3>
                        </div>
                        <div>
                            <p className="text-sm text-gray-500 mb-4">
                                {t('salon.hours.time_step_desc')}
                            </p>
                            <CustomSelect
                                value={organization.step?.toString() || '30'}
                                onChange={async (val) => {
                                    const newStep = parseInt(val, 10);
                                    const res = await updateOrganization({ ...organization, step: newStep });
                                    if (res.success) {
                                        toast.success(t('salon.hours.time_step_updated'));
                                    } else {
                                        toast.error(t('salon.hours.time_step_error'));
                                    }
                                }}
                                options={Array.from({ length: (60 - 10) / 5 + 1 }, (_, i) => {
                                    const val = (10 + i * 5).toString();
                                    return {
                                        value: val,
                                        label: t('salon.hours.minutes_label', { minutes: val })
                                    };
                                })}
                                placeholder={t('salon.hours.time_step_placeholder')}
                            />
                        </div>
                    </div>
                )}

                {/* Ferie */}
                {!readOnly && (
                    <div className={`bg-white rounded-3xl border border-gray-100 shadow-sm p-8 h-fit ${readOnly ? 'opacity-90' : ''}`}>
                        <div className="flex justify-between items-center mb-6">
                            <h3 className="text-xl font-bold text-gray-900 flex items-center gap-2"><CalendarIcon className="text-orange-500" size={20} /> {t('salon.hours.holidays')}</h3>
                        </div>
                        {!readOnly && (
                            <div className="bg-gray-50 rounded-2xl p-4 space-y-3">
                                <DatePicker selected={start} onSelect={setStart} placeholder={t('staff.timeoff.from') + '...'} allowClosedDays={true} />
                                <DatePicker selected={end} onSelect={setEnd} placeholder={t('staff.timeoff.to') + '...'} minDate={start} allowClosedDays={true} />
                                <button onClick={addHoliday} disabled={isAddingHoliday} className="w-full bg-gray-900 text-white rounded-xl py-3 font-bold text-sm hover:bg-black transition-colors flex justify-center items-center gap-2 cursor-pointer">
                                    {isAddingHoliday ? <Loader2 className="animate-spin" size={16} /> : <Plus size={16} />} {t('common.add')}
                                </button>
                            </div>
                        )}
                        <div className="space-y-3">
                            {holidays.map((h: any) => (
                                <div key={h.id} className="bg-orange-50/50 p-4 rounded-xl border border-orange-100 relative group">
                                    <h4 className="font-bold text-gray-800 text-sm">Chiusura</h4>
                                    <p className="text-xs text-gray-500 mt-1">{format(new Date(h.start_date), 'dd MMM')} - {format(new Date(h.end_date), 'dd MMM yyyy')}</p>
                                    {!readOnly && (
                                        <button onClick={() => setHolidayToDelete(h.id)} className="absolute top-2 right-2 p-1.5 text-red-600 hover:bg-red-50 rounded-md transition-all cursor-pointer"><Trash2 size={14} /></button>
                                    )}
                                </div>
                            ))}
                        </div>
                    </div>
                )}
            </div>

            <ConfirmDialog isOpen={!!holidayToDelete} title={t('salon.services.delete.title')} description={t('salon.services.delete.description', { name: 'Holiday' })} confirmLabel={t('common.delete')} onConfirm={deleteHoliday} onCancel={() => setHolidayToDelete(null)} />
        </motion.div>
    );
};

export default SalonHours;